import React, { memo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { PiCheck, PiCaretRight, PiWarningCircle, PiArrowClockwise, PiCopy, PiStopCircle } from 'react-icons/pi';
import Markdown, { plainText } from './Markdown';

/* One answer: what the assistant checked (live, then folded), the photos it looked
 * at, the answer itself, and how it ended if it did not end well. */

const spin = keyframes`to { transform: rotate(360deg); }`;
const blink = keyframes`0%, 45% { opacity: 1; } 55%, 100% { opacity: 0; }`;

const Turn = styled.article`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const Spinner = styled.span`
  width: 12px;
  height: 12px;
  flex: none;
  border-radius: 50%;
  border: 1.5px solid ${p => p.theme.colors.borderHover};
  border-top-color: ${p => p.theme.colors.textSecondary};
  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 0.8s linear infinite;
  }
`;

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 1.35;
    color: ${p => p.theme.colors.textMuted};
    min-width: 0;
  }
  li[data-active='true'] { color: ${p => p.theme.colors.textSecondary}; }
  li > svg { flex: none; font-size: 12px; }
  li > span:last-child { min-width: 0; overflow-wrap: anywhere; }
  ${p => p.$folded && css`
    margin-top: 6px;
    padding-left: 2px;
  `}
`;

const Fold = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 2px 8px 2px 4px;
  margin-left: -4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${p => p.theme.colors.textMuted};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  svg { font-size: 12px; transition: transform 160ms ease-out; }
  &[aria-expanded='true'] svg { transform: rotate(90deg); }
  &:hover { color: ${p => p.theme.colors.textSecondary}; background: ${p => p.theme.colors.surfaceHover}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
`;

const Model = styled.li`
  && { color: ${p => p.theme.colors.textMuted}; font-size: 12px; padding-left: 20px; }
`;

const Photos = styled.figure`
  margin: 0;
  min-width: 0;
  figcaption {
    font-size: 13px;
    color: ${p => p.theme.colors.textMuted};
    margin-bottom: 6px;
  }
  figcaption a { color: ${p => p.theme.colors.textSecondary}; font-weight: 600; text-underline-offset: 2px; }
  .row {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: thin;
    padding-bottom: 2px;
  }
`;

const Thumb = styled.button`
  flex: none;
  width: 76px;
  height: 76px;
  min-height: 0;
  padding: 0;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  overflow: hidden;
  background: ${p => p.theme.colors.surfaceHover};
  cursor: zoom-in;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &:hover { border-color: ${p => p.theme.colors.borderHover}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
`;

export const Prose = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: ${p => p.theme.colors.text};
  overflow-wrap: anywhere;
  min-width: 0;

  > :first-child { margin-top: 0; }
  > :last-child { margin-bottom: 0; }
  p { margin: 0 0 0.7em; }
  h3, h4 { line-height: 1.3; letter-spacing: -0.01em; text-wrap: balance; }
  h3 { font-size: 16px; font-weight: 650; margin: 1.15em 0 0.4em; }
  h4 { font-size: 15px; font-weight: 600; margin: 1em 0 0.3em; }
  strong { font-weight: 650; }
  ul, ol { margin: 0 0 0.75em; padding-left: 1.35em; }
  li { margin: 0.22em 0; padding-left: 0.1em; }
  li[data-level='1'] { margin-left: 1.25em; }
  li::marker { color: ${p => p.theme.colors.textMuted}; }
  a {
    color: ${p => p.theme.colors.primary};
    font-weight: 550;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    min-height: 0;
  }
  a:hover { text-decoration-thickness: 2px; }
  code {
    font-family: ${p => p.theme.fonts.mono};
    font-size: 0.86em;
    padding: 0.1em 0.35em;
    border-radius: 5px;
    background: ${p => p.theme.colors.surfaceHover};
  }
  pre {
    margin: 0 0 0.75em;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${p => p.theme.colors.surfaceHover};
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.5;
  }
  pre code { padding: 0; background: none; font-size: inherit; }
  blockquote {
    margin: 0 0 0.75em;
    padding-left: 12px;
    border-left: 2px solid ${p => p.theme.colors.borderHover};
    color: ${p => p.theme.colors.textSecondary};
  }
  hr { border: 0; border-top: 1px solid ${p => p.theme.colors.border}; margin: 1em 0; }
  .table {
    margin: 0 0 0.85em;
    overflow-x: auto;
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: 10px;
  }
  .table:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
  table { border-collapse: collapse; width: 100%; font-size: 13.5px; line-height: 1.4; font-variant-numeric: tabular-nums; }
  th, td { padding: 7px 10px; text-align: left; vertical-align: top; }
  th { font-weight: 600; color: ${p => p.theme.colors.textSecondary}; background: ${p => p.theme.colors.surfaceHover}; white-space: nowrap; }
  td { border-top: 1px solid ${p => p.theme.colors.border}; }

  /* The caret says "still writing"; it goes when the answer ends. */
  .caret {
    display: inline-block;
    width: 7px;
    height: 1.05em;
    margin-left: 2px;
    vertical-align: -0.15em;
    border-radius: 1px;
    background: ${p => p.theme.colors.textMuted};
    @media (prefers-reduced-motion: no-preference) {
      animation: ${blink} 1s steps(1) infinite;
    }
  }
`;

const Outcome = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.45;
  ${p => (p.$tone === 'error'
    ? css`background: ${p.theme.colors.dangerLight}; color: ${p.theme.colors.text};`
    : css`background: ${p.theme.colors.surfaceHover}; color: ${p.theme.colors.textSecondary};`)}
  > svg { flex: none; font-size: 18px; margin-top: 1px; color: ${p => (p.$tone === 'error' ? p.theme.colors.danger : p.theme.colors.textMuted)}; }
  .msg { flex: 1 1 180px; min-width: 0; }
`;

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 4px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.text};
  font: inherit;
  font-size: 13px;
  font-weight: 550;
  cursor: pointer;
  white-space: nowrap;
  svg { font-size: 14px; }
  &:hover:not(:disabled) { background: ${p => p.theme.colors.surfaceHover}; border-color: ${p => p.theme.colors.borderHover}; }
  &:disabled { color: ${p => p.theme.colors.textMuted}; cursor: default; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
`;

const Quiet = styled(Action)`
  align-self: flex-start;
  border-color: transparent;
  background: transparent;
  color: ${p => p.theme.colors.textMuted};
  font-weight: 500;
  margin-left: -10px;
  &:hover:not(:disabled) { color: ${p => p.theme.colors.textSecondary}; border-color: transparent; }
`;

function Trail({ steps, live, writing, model }) {
  const [open, setOpen] = useState(false);

  if (live) {
    // Nothing reported yet: one "Thinking" row so the wait never looks frozen.
    if (!steps.length) {
      return writing ? null : (
        <Steps aria-hidden="true"><li data-active="true"><Spinner /><span>Thinking</span></li></Steps>
      );
    }
    return (
      <Steps aria-hidden="true">
        {steps.map((s, i) => {
          const active = i === steps.length - 1 && !writing;
          return (
            <li key={i} data-active={active}>
              {active ? <Spinner /> : <PiCheck />}
              <span>{s.label}</span>
            </li>
          );
        })}
      </Steps>
    );
  }

  if (!steps.length) return null;
  const n = steps.length;
  return (
    <div>
      <Fold type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <PiCaretRight aria-hidden="true" />
        Checked {n} {n === 1 ? 'source' : 'sources'}
      </Fold>
      {open && (
        <Steps $folded>
          {steps.map((s, i) => <li key={i}><PiCheck aria-hidden="true" /><span>{s.label}</span></li>)}
          {model && <Model>Answered by {model}</Model>}
        </Steps>
      )}
    </div>
  );
}

function PhotoStrip({ group, onOpen }) {
  const [failed, setFailed] = useState(() => new Set());
  const shown = group.images.filter(img => !failed.has(img.thumb || img.full));
  if (!shown.length) return null;
  const id = group.plant_id;
  const issueId = /^Issue #(\d+)$/.exec(group.label || '')?.[1];
  return (
    <Photos>
      <figcaption>
        {id ? <>Photos of <Link to={`/plant/${id}`}>{id}</Link></>
          : issueId ? <>{shown.length} photo{shown.length === 1 ? '' : 's'} on <Link to={`/issues/${issueId}`}>issue #{issueId}</Link></>
            : 'Photos it looked at'}
      </figcaption>
      <div className="row">
        {shown.map((img, i) => (
          <Thumb key={img.full || img.thumb} type="button"
            aria-label={`Open photo ${i + 1} of ${shown.length}${id ? ` of plant ${id}` : ''}`}
            onClick={() => onOpen(shown, i, id, group.label)}>
            <img src={img.thumb || img.full} alt="" loading="lazy" decoding="async"
              onError={() => setFailed(f => new Set(f).add(img.thumb || img.full))} />
          </Thumb>
        ))}
      </div>
    </Photos>
  );
}

function AssistantTurn({ message, onRetry, onOpenPhotos, canRetry }) {
  const { content, steps = [], photos = [], status, error, model } = message;
  const live = status === 'streaming';
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText(content));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { /* clipboard blocked (http, permissions): nothing to do */ }
  };

  return (
    <Turn aria-busy={live} aria-label="Assistant">
      <Trail steps={steps} live={live} writing={!!content && (!steps.length || content.length > steps[steps.length - 1].at)} model={model} />

      {photos.map(g => <PhotoStrip key={g.label || g.plant_id || 'photos'} group={g} onOpen={onOpenPhotos} />)}

      {content && (
        <Prose>
          <Markdown text={content} />
          {live && <span className="caret" aria-hidden="true" />}
        </Prose>
      )}

      {status === 'error' && (
        <Outcome $tone="error" role="alert">
          <PiWarningCircle aria-hidden="true" />
          <span className="msg">
            {content ? 'The answer was cut short. ' : ''}{error}
          </span>
          {canRetry && <Action type="button" onClick={() => onRetry(message.id)}><PiArrowClockwise aria-hidden="true" />Try again</Action>}
        </Outcome>
      )}

      {status === 'stopped' && (
        <Outcome>
          <PiStopCircle aria-hidden="true" />
          <span className="msg">{content ? 'Stopped before the end.' : 'Stopped before it answered.'}</span>
          {canRetry && <Action type="button" onClick={() => onRetry(message.id)}><PiArrowClockwise aria-hidden="true" />Ask again</Action>}
        </Outcome>
      )}

      {status === 'done' && content && (
        <Quiet type="button" onClick={copy} aria-live="polite">
          {copied ? <PiCheck aria-hidden="true" /> : <PiCopy aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy answer'}
        </Quiet>
      )}
    </Turn>
  );
}

export default memo(AssistantTurn);
